import React, { memo, useMemo } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
  useMediaQuery, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatCurrency } from '../../utils';
import { vendorDisplayLabel } from '../../utils/rfqEntity';
import { IPD_COLORS } from '../../theme';

function ComparisonDesktopTable({ items, vendorMap, resolveItemLabel }) {
  const vendorIds = useMemo(() => {
    const ids = new Set();
    (items || []).forEach((row) => {
      (row.quotes || []).forEach((q) => {
        if (q.vendor_id) ids.add(q.vendor_id);
      });
    });
    return Array.from(ids);
  }, [items]);

  if (!items?.length) {
    return (
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2, textAlign: 'center' }}>
        No comparison data available.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 640, '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 180 }}>Item</TableCell>
            <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
            {vendorIds.map((vid) => (
              <TableCell key={vid} align="right" sx={{ color: IPD_COLORS.textSecondary, minWidth: 110 }}>
                {vendorMap[vid] || vid}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row) => (
            <TableRow key={row.rfq_item_id || row.item_id}>
              <TableCell>{resolveItemLabel(row)}</TableCell>
              <TableCell align="right">{row.requested_quantity}</TableCell>
              {vendorIds.map((vid) => {
                const quote = (row.quotes || []).find((q) => q.vendor_id === vid);
                return (
                  <TableCell key={vid} align="right">
                    {quote ? (
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {formatCurrency(quote.unit_rate)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                          Total {formatCurrency(quote.line_total)}
                        </Typography>
                      </Box>
                    ) : '—'}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ComparisonMobileAccordions({ items, vendorMap, resolveItemLabel }) {
  if (!items?.length) {
    return (
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2, textAlign: 'center' }}>
        No comparison data available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((row, i) => (
        <Accordion
          key={row.rfq_item_id || row.item_id || i}
          disableGutters
          sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px !important', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{resolveItemLabel(row)}</Typography>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>Qty {row.requested_quantity}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {(row.quotes || []).length === 0 ? (
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>No quotes for this item.</Typography>
            ) : (row.quotes || []).map((q) => (
              <Box key={q.vendor_quote_id || q.vendor_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: 13 }}>{vendorMap[q.vendor_id] || q.vendor_id}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {formatCurrency(q.unit_rate)} / {formatCurrency(q.line_total)}
                </Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function RfqQuoteComparisonTable({
  comparison,
  loading,
  error,
  vendorDirectory = [],
  resolveItem,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const items = comparison?.items || [];
  const vendorMap = useMemo(() => {
    const map = {};
    (vendorDirectory || []).forEach((v) => {
      const id = v.vendor_id || v.id;
      if (id) map[id] = vendorDisplayLabel(v);
    });
    (comparison?.vendor_quotes || []).forEach((q) => {
      if (q.vendor_id && q.vendor_name) map[q.vendor_id] = q.vendor_name;
    });
    return map;
  }, [vendorDirectory, comparison]);

  function resolveItemLabel(row) {
    if (resolveItem) {
      return resolveItem({ item_id: row.item_id, id: row.rfq_item_id }) || row.item_id || '—';
    }
    return row.item_id || '—';
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return isMobile ? (
    <ComparisonMobileAccordions items={items} vendorMap={vendorMap} resolveItemLabel={resolveItemLabel} />
  ) : (
    <ComparisonDesktopTable items={items} vendorMap={vendorMap} resolveItemLabel={resolveItemLabel} />
  );
}

export default memo(RfqQuoteComparisonTable);
